import { useEffect, useState } from 'react';
import { apiClient } from '@copro/api';
import { enqueueSnackbar } from '../../../../components/GlobalSnackbar/snackbarStore';
import useGetDriveConfig from '../../../google-drive/api/useGetDriveConfig';
import { useGetDocumentTypes } from '../../../documents/api/useGetDocumentTypes';
import type { CreateDocumentAction } from '../../types/inbox.types';

type Props = {
  open: boolean;
  onClose: () => void;
  action: CreateDocumentAction | null;
  spaceId: string;
  onSuccess: () => void;
};

export function useCreateDocumentFromInboxModal(props: Props) {
  const { data: driveConfig } = useGetDriveConfig();
  const { data: documentTypes = [] } = useGetDocumentTypes(props.spaceId);

  const [title, setTitle] = useState('');
  const [subTopicId, setSubTopicId] = useState('');
  const [documentTypeId, setDocumentTypeId] = useState('');
  const [providerId, setProviderId] = useState('');
  const [amount, setAmount] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isQuote = Boolean(props.action?.isQuote);

  useEffect(() => {
    if (props.action) {
      setTitle(props.action.title || props.action.filename);
      setSubTopicId(props.action.suggestedSubTopicId || '');
      setDocumentTypeId(props.action.suggestedDocumentTypeId || '');
      setProviderId(props.action.suggestedProviderId || '');
      setAmount(props.action.suggestedAmount ? String(props.action.suggestedAmount) : '');
    }
  }, [props.action]);

  async function handleConfirm() {
    if (!props.action || !title.trim()) return;
    setIsSubmitting(true);

    try {
      let fileUrl: string | null = null;

      // 1. Upload to Google Drive if configured
      if (driveConfig) {
        try {
          const { data: fileBlob, error: downloadError } = await apiClient.storage
            .from('inbox-attachments')
            .download(props.action.storagePath);

          if (!downloadError && fileBlob) {
            const file = new File([fileBlob], props.action.filename, {
              type: fileBlob.type || 'application/octet-stream',
            });

            const formData = new FormData();
            formData.append('files', file);
            formData.append('spaceId', props.spaceId);
            if (subTopicId) {
              formData.append('subjectId', subTopicId);
            }

            const { data: uploadResults, error: uploadError } = await apiClient.functions.invoke(
              'upload-to-drive',
              { body: formData }
            );

            const results = uploadResults as { viewUrl?: string }[] | null;
            if (!uploadError && Array.isArray(results) && results[0]?.viewUrl) {
              fileUrl = results[0].viewUrl;
            }
          }
        } catch (driveErr) {
          console.warn('Upload Google Drive échoué, fallback sur stockage interne:', driveErr);
        }
      }

      // 2. Fallback to Supabase Storage signed URL
      if (!fileUrl) {
        const { data: signedData } = await apiClient.storage
          .from('inbox-attachments')
          .createSignedUrl(props.action.storagePath, 60 * 60 * 24 * 365);
        fileUrl = signedData?.signedUrl || props.action.storagePath;
      }

      // 3. If it's a quote, create subject_quotes entry first
      let quoteId: string | null = null;
      if (isQuote && subTopicId) {
        const parsedAmount = amount ? parseFloat(amount.replace(',', '.')) : null;

        const { data: quoteData, error: quoteError } = await apiClient
          .from('subject_quotes')
          .insert({
            space_id: props.spaceId,
            subject_id: subTopicId,
            provider_id: providerId || null,
            provider_name: props.action.suggestedProviderName || null,
            title: title.trim(),
            amount: parsedAmount && !isNaN(parsedAmount) ? parsedAmount : null,
            date_received: new Date().toISOString().split('T')[0],
            status: 'pending',
          })
          .select('id')
          .single();

        if (quoteError) throw quoteError;
        if (quoteData) {
          quoteId = quoteData.id;
        }
      }

      // 4. Insert into documents table
      const { error: insertError } = await apiClient.from('documents').insert({
        space_id: props.spaceId,
        name: title.trim(),
        file_url: fileUrl,
        sub_topic_id: subTopicId || null,
        document_type_id: documentTypeId || null,
        provider_id: providerId || null,
        quote_id: quoteId,
      });

      if (insertError) throw insertError;

      const successMsg = isQuote
        ? `Devis « ${title} » enregistré et ajouté au sujet !`
        : `Document « ${title} » enregistré avec succès !`;
      enqueueSnackbar(successMsg, 'success');
      props.onSuccess();
      props.onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur lors de la création du document';
      enqueueSnackbar(msg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handlePreviewFile() {
    if (!props.action?.storagePath) return;
    try {
      const { data, error } = await apiClient.storage
        .from('inbox-attachments')
        .createSignedUrl(props.action.storagePath, 3600);

      if (error) throw error;
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank');
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Impossible de prévisualiser le document';
      enqueueSnackbar(msg, 'error');
    }
  }

  return {
    title,
    setTitle,
    subTopicId,
    setSubTopicId,
    documentTypeId,
    setDocumentTypeId,
    providerId,
    setProviderId,
    amount,
    setAmount,
    isQuote,
    isSubmitting,
    documentTypes,
    handleConfirm,
    handlePreviewFile,
  };
}
